// ============================================================================
// PRODUCT ASSIGNMENT MODAL
// Modal for managing supplier-product relationships from supplier perspective
// Allows assigning multiple products to a single supplier
// Date: May 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Plus,
  Trash2,
  Star,
  Check,
  AlertCircle,
  Loader2,
  Package,
  Building2,
  Clock,
  Box,
  DollarSign,
  Search,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';

interface InventoryItem {
  id: string;
  title: string;
  sku?: string | null;
  item_subtype: string;
}

interface SupplierRelationship {
  id?: number;
  work_item_id: string;
  product_name?: string | null;
  product_sku?: string | null;
  supplier_sku?: string | null;
  cost_price?: number | null;
  currency_code?: string | null;
  lead_time_days?: number | null;
  min_order_qty?: number | null;
  pack_size?: number | null;
  is_preferred: boolean;
  priority_rank?: number | null;
  selection_reason?: string | null;
  fallback_reason?: string | null;
  notes?: string | null;
}

interface ProductAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplierContactId: number;
  supplierName: string;
  onSuccess?: () => void;
}

const CURRENCY_OPTIONS = ['ZAR', 'USD', 'EUR', 'GBP', 'AUD', 'NZD', 'CAD'];

export default function ProductAssignmentModal({
  isOpen,
  onClose,
  supplierContactId,
  supplierName,
  onSuccess,
}: ProductAssignmentModalProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState<InventoryItem[]>([]);
  const [relationships, setRelationships] = useState<SupplierRelationship[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state for new relationship
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [newRelationship, setNewRelationship] = useState<Partial<SupplierRelationship>>({
    supplier_sku: '',
    cost_price: null,
    currency_code: 'ZAR',
    lead_time_days: null,
    min_order_qty: null,
    pack_size: null,
    is_preferred: false,
    priority_rank: null,
    selection_reason: '',
    fallback_reason: '',
    notes: '',
  });

  // Load available inventory products
  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/work-items?item_type=inventory&item_subtype=product', {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await response.json();
      if (!response.ok || json?.success === false) {
        throw new Error(json?.error || 'Failed to load products');
      }

      setProducts(Array.isArray(json?.data) ? json.data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
      console.error('[ProductAssignmentModal] failed to load products', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load existing relationships
  const loadRelationships = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/work-items/inventory/suppliers?supplier_contact_id=${supplierContactId}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await response.json();
      if (!response.ok || json?.success === false) {
        throw new Error(json?.error || 'Failed to load product relationships');
      }

      setRelationships(Array.isArray(json?.data) ? json.data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product relationships');
      console.error('[ProductAssignmentModal] failed to load relationships', err);
    } finally {
      setLoading(false);
    }
  }, [supplierContactId]);

  useEffect(() => {
    if (isOpen) {
      void loadProducts();
      void loadRelationships();
    }
  }, [isOpen, loadProducts, loadRelationships]);

  const handleAddRelationship = async () => {
    if (!selectedProductId) {
      notify.error('Please select a product');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/work-items/inventory/suppliers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          work_item_id: selectedProductId,
          supplier_contact_id: supplierContactId,
          supplier_sku: newRelationship.supplier_sku || null,
          cost_price: newRelationship.cost_price || null,
          currency_code: newRelationship.currency_code || 'ZAR',
          lead_time_days: newRelationship.lead_time_days || null,
          min_order_qty: newRelationship.min_order_qty || null,
          pack_size: newRelationship.pack_size || null,
          is_preferred: newRelationship.is_preferred || false,
          priority_rank: newRelationship.priority_rank || null,
          selection_reason: newRelationship.selection_reason || null,
          fallback_reason: newRelationship.fallback_reason || null,
          notes: newRelationship.notes || null,
        }),
      });

      const json = await response.json();
      if (!response.ok || json?.success === false) {
        throw new Error(json?.error || 'Failed to add product relationship');
      }

      notify.success('Product relationship added successfully');
      await loadRelationships();
      setSelectedProductId(null);
      setNewRelationship({
        supplier_sku: '',
        cost_price: null,
        currency_code: 'ZAR',
        lead_time_days: null,
        min_order_qty: null,
        pack_size: null,
        is_preferred: false,
        priority_rank: null,
        selection_reason: '',
        fallback_reason: '',
        notes: '',
      });
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add product relationship');
      notify.error(err instanceof Error ? err.message : 'Failed to add product relationship');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRelationship = async (id: number) => {
    if (!(await confirm({ message: 'Are you sure you want to remove this product relationship?', variant: 'danger' }))) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/work-items/inventory/suppliers/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await response.json();
      if (!response.ok || json?.success === false) {
        throw new Error(json?.error || 'Failed to remove product relationship');
      }

      notify.success('Product relationship removed successfully');
      await loadRelationships();
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove product relationship');
      notify.error(err instanceof Error ? err.message : 'Failed to remove product relationship');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPreferred = async (id: number) => {
    setSaving(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/work-items/inventory/suppliers/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          is_preferred: true,
        }),
      });

      const json = await response.json();
      if (!response.ok || json?.success === false) {
        throw new Error(json?.error || 'Failed to set preferred product');
      }

      notify.success('Preferred product updated successfully');
      await loadRelationships();
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set preferred product');
      notify.error(err instanceof Error ? err.message : 'Failed to set preferred product');
    } finally {
      setSaving(false);
    }
  };

  // Filter products based on search term and exclude already assigned
  const availableProducts = products.filter(
    (p) => 
      p.item_subtype === 'product' &&
      !relationships.some((r) => r.work_item_id === p.id) &&
      (p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
       (p.sku && p.sku.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Manage Products</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {supplierName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            disabled={saving}
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Add New Relationship */}
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add Product
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Product Selection */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Product *
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Search products..."
                        disabled={saving}
                      />
                    </div>
                    {selectedProductId && (
                      <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Selected: {products.find(p => p.id === selectedProductId)?.title}
                      </div>
                    )}
                    {!selectedProductId && availableProducts.length > 0 && searchTerm && (
                      <div className="mt-2 max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
                        {availableProducts.slice(0, 10).map((product) => (
                          <button
                            key={product.id}
                            onClick={() => {
                              setSelectedProductId(product.id);
                              setSearchTerm('');
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            disabled={saving}
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">{product.title}</div>
                            {product.sku && <div className="text-xs text-gray-500">SKU: {product.sku}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Supplier SKU */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Supplier SKU
                    </label>
                    <input
                      type="text"
                      value={newRelationship.supplier_sku || ''}
                      onChange={(e) => setNewRelationship({ ...newRelationship, supplier_sku: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your SKU for this product"
                      disabled={saving}
                    />
                  </div>

                  {/* Cost Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Cost Price
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.01"
                        value={newRelationship.cost_price || ''}
                        onChange={(e) => setNewRelationship({ ...newRelationship, cost_price: e.target.value ? Number(e.target.value) : null })}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                        disabled={saving}
                      />
                      <select
                        value={newRelationship.currency_code || 'ZAR'}
                        onChange={(e) => setNewRelationship({ ...newRelationship, currency_code: e.target.value })}
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={saving}
                      >
                        {CURRENCY_OPTIONS.map((curr) => (
                          <option key={curr} value={curr}>{curr}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Lead Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Lead Time (days)
                    </label>
                    <input
                      type="number"
                      value={newRelationship.lead_time_days || ''}
                      onChange={(e) => setNewRelationship({ ...newRelationship, lead_time_days: e.target.value ? Number(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      disabled={saving}
                    />
                  </div>

                  {/* Min Order Qty */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Min Order Qty
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={newRelationship.min_order_qty || ''}
                      onChange={(e) => setNewRelationship({ ...newRelationship, min_order_qty: e.target.value ? Number(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      disabled={saving}
                    />
                  </div>

                  {/* Pack Size */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Pack Size
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={newRelationship.pack_size || ''}
                      onChange={(e) => setNewRelationship({ ...newRelationship, pack_size: e.target.value ? Number(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                      disabled={saving}
                    />
                  </div>

                  {/* Priority Rank */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Priority Rank
                    </label>
                    <input
                      type="number"
                      value={newRelationship.priority_rank || ''}
                      onChange={(e) => setNewRelationship({ ...newRelationship, priority_rank: e.target.value ? Number(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1 = highest priority"
                      disabled={saving}
                    />
                  </div>

                  {/* Is Preferred */}
                  <div className="flex items-center gap-2 mt-6">
                    <input
                      type="checkbox"
                      id="is_preferred"
                      checked={newRelationship.is_preferred || false}
                      onChange={(e) => setNewRelationship({ ...newRelationship, is_preferred: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      disabled={saving}
                    />
                    <label htmlFor="is_preferred" className="text-sm text-gray-700 dark:text-gray-300">
                      Mark as preferred product
                    </label>
                  </div>

                  {/* Selection Reason */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Selection Reason
                    </label>
                    <textarea
                      value={newRelationship.selection_reason || ''}
                      onChange={(e) => setNewRelationship({ ...newRelationship, selection_reason: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={2}
                      placeholder="Why this product is selected..."
                      disabled={saving}
                    />
                  </div>

                  {/* Notes */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={newRelationship.notes || ''}
                      onChange={(e) => setNewRelationship({ ...newRelationship, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={2}
                      placeholder="Additional notes..."
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleAddRelationship}
                    disabled={saving || !selectedProductId}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Add Product
                  </button>
                </div>
              </div>

              {/* Existing Relationships */}
              {relationships.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Current Products ({relationships.length})
                  </h3>
                  <div className="space-y-3">
                    {relationships.map((rel) => (
                      <div
                        key={rel.id}
                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {rel.is_preferred && (
                                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              )}
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                                {rel.product_name || rel.product_sku || rel.work_item_id}
                              </h4>
                              {rel.supplier_sku && (
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  Your SKU: {rel.supplier_sku}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              {rel.cost_price && (
                                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                  <DollarSign className="w-3 h-3" />
                                  <span>{rel.currency_code || 'ZAR'} {rel.cost_price.toFixed(2)}</span>
                                </div>
                              )}
                              {rel.lead_time_days && (
                                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                  <Clock className="w-3 h-3" />
                                  <span>{rel.lead_time_days} days</span>
                                </div>
                              )}
                              {rel.min_order_qty && (
                                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                  <Box className="w-3 h-3" />
                                  <span>MOQ: {rel.min_order_qty}</span>
                                </div>
                              )}
                              {rel.priority_rank && (
                                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                  <span>Rank: {rel.priority_rank}</span>
                                </div>
                              )}
                            </div>
                            {rel.selection_reason && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 italic">
                                "{rel.selection_reason}"
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            {!rel.is_preferred && rel.id && (
                              <button
                                onClick={() => rel.id && handleSetPreferred(rel.id)}
                                disabled={saving}
                                className="p-2 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"
                                title="Set as preferred"
                              >
                                <Star className="w-4 h-4 text-gray-400 hover:text-yellow-500" />
                              </button>
                            )}
                            {rel.id && (
                              <button
                                onClick={() => handleDeleteRelationship(rel.id!)}
                                disabled={saving}
                                className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Remove product"
                              >
                                <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {relationships.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No products assigned yet</p>
                  <p className="text-sm">Add products above to manage your catalog</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
